import React, { useState, useEffect } from 'react';
import { Plus, Package, CheckCircle, Trash2, Edit2, X } from 'lucide-react';

const MaterialPendiente = () => {
  const [activeTab, setActiveTab] = useState('pendiente');
  const [entregas, setEntregas] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    cliente: '',
    descripcion: '',
    cantidad: '',
    fecha_entrega: '',
    observaciones: ''
  });

  useEffect(() => {
    fetchEntregas();
  }, []);

  const fetchEntregas = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/material-pendiente`);
      const data = await res.json();
      if (data.success) setEntregas(data.entregas || []);
    } catch (e) {
      console.error('Error:', e);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingId 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/material-pendiente/${editingId}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/material-pendiente`;
      const method = editingId ? 'PUT' : 'POST';
      
      await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      setShowModal(false);
      setEditingId(null);
      setFormData({ cliente: '', descripcion: '', cantidad: '', fecha_entrega: '', observaciones: '' });
      fetchEntregas();
    } catch (e) {
      console.error('Error:', e);
    }
  };

  const toggleCompletado = async (id, completado) => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/material-pendiente/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ completado: !completado })
      });
      fetchEntregas();
    } catch (e) {
      console.error('Error:', e);
    }
  };

  const deleteEntrega = async (id) => {
    if (!window.confirm('¿Eliminar esta entrega?')) return;
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/material-pendiente/${id}`, { method: 'DELETE' });
      fetchEntregas();
    } catch (e) {
      console.error('Error:', e);
    }
  };

  const editEntrega = (entrega) => {
    setFormData({
      cliente: entrega.cliente,
      descripcion: entrega.descripcion,
      cantidad: entrega.cantidad,
      fecha_entrega: entrega.fecha_entrega,
      observaciones: entrega.observaciones || ''
    });
    setEditingId(entrega.id);
    setShowModal(true);
  };

  const pendientes = entregas.filter(e => !e.completado);
  const completados = entregas.filter(e => e.completado);

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Package className="text-amber-600" /> Material Pendiente
          </h1>
          <button
            onClick={() => { setShowModal(true); setEditingId(null); setFormData({ cliente: '', descripcion: '', cantidad: '', fecha_entrega: '', observaciones: '' }); }}
            className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <Plus size={20} /> Nueva Entrega
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setActiveTab('pendiente')}
            className={`px-4 py-2 rounded-lg font-medium ${activeTab === 'pendiente' ? 'bg-amber-500 text-white' : 'bg-white text-gray-600'}`}
          >
            Material por Entregar ({pendientes.length})
          </button>
          <button
            onClick={() => setActiveTab('completado')}
            className={`px-4 py-2 rounded-lg font-medium ${activeTab === 'completado' ? 'bg-green-500 text-white' : 'bg-white text-gray-600'}`}
          >
            Completado ({completados.length})
          </button>
        </div>

        {/* Lista */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          {(activeTab === 'pendiente' ? pendientes : completados).length === 0 ? (
            <div className="p-8 text-center text-gray-500">No hay entregas {activeTab === 'pendiente' ? 'pendientes' : 'completadas'}</div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-3 text-left">Cliente</th>
                  <th className="p-3 text-left">Descripción</th>
                  <th className="p-3 text-left">Cantidad</th>
                  <th className="p-3 text-left">Fecha Entrega</th>
                  <th className="p-3 text-left">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {(activeTab === 'pendiente' ? pendientes : completados).map(entrega => (
                  <tr key={entrega.id} className="border-t hover:bg-gray-50">
                    <td className="p-3 font-medium">{entrega.cliente}</td>
                    <td className="p-3">{entrega.descripcion}</td>
                    <td className="p-3">{entrega.cantidad}</td>
                    <td className="p-3">{entrega.fecha_entrega}</td>
                    <td className="p-3 flex gap-2">
                      <button onClick={() => toggleCompletado(entrega.id, entrega.completado)} className={`p-2 rounded ${entrega.completado ? 'bg-amber-100 text-amber-600' : 'bg-green-100 text-green-600'}`} title={entrega.completado ? 'Marcar pendiente' : 'Marcar completado'}>
                        <CheckCircle size={18} />
                      </button>
                      <button onClick={() => editEntrega(entrega)} className="p-2 rounded bg-blue-100 text-blue-600"><Edit2 size={18} /></button>
                      <button onClick={() => deleteEntrega(entrega.id)} className="p-2 rounded bg-red-100 text-red-600"><Trash2 size={18} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">{editingId ? 'Editar' : 'Nueva'} Entrega</h2>
              <button onClick={() => setShowModal(false)}><X size={24} /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <input type="text" placeholder="Cliente" value={formData.cliente} onChange={e => setFormData({...formData, cliente: e.target.value})} className="w-full p-3 border rounded-lg" required />
              <input type="text" placeholder="Descripción del material" value={formData.descripcion} onChange={e => setFormData({...formData, descripcion: e.target.value})} className="w-full p-3 border rounded-lg" required />
              <input type="text" placeholder="Cantidad" value={formData.cantidad} onChange={e => setFormData({...formData, cantidad: e.target.value})} className="w-full p-3 border rounded-lg" required />
              <input type="date" value={formData.fecha_entrega} onChange={e => setFormData({...formData, fecha_entrega: e.target.value})} className="w-full p-3 border rounded-lg" required />
              <textarea placeholder="Observaciones" value={formData.observaciones} onChange={e => setFormData({...formData, observaciones: e.target.value})} className="w-full p-3 border rounded-lg" rows={2} />
              <button type="submit" className="w-full bg-amber-500 hover:bg-amber-600 text-white py-3 rounded-lg font-medium">
                {editingId ? 'Actualizar' : 'Guardar'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaterialPendiente;